import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, List, ListItem, ListItemText, LinearProgress } from '@mui/material';

type Step = { afterSeconds: number; text: string };

const DEFAULT_STEPS: Step[] = [
  { afterSeconds: 0, text: 'Job accepted by queue service' },
  { afterSeconds: 5, text: 'Worker reserved job slot' },
  { afterSeconds: 15, text: 'Loading entity details from database' },
  { afterSeconds: 25, text: 'Retrieving relevant knowledge base context (DRIFT)' },
  { afterSeconds: 45, text: 'Generating course sections (What / Why / Sell…)' },
  { afterSeconds: 90, text: 'Generating Configure / Validate sections' },
  { afterSeconds: 140, text: 'Generating Differentiate / Troubleshoot sections' },
  { afterSeconds: 190, text: 'Building Knowledge Check quiz' },
  { afterSeconds: 220, text: 'Assembling Moodle course structure' },
  { afterSeconds: 245, text: 'Saving draft course to database' },
  { afterSeconds: 260, text: 'Finalizing and validating output' },
];

function useAnimatedDots(active: boolean, intervalMs = 450) {
  const [dots, setDots] = useState('');
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setDots((d) => (d.length >= 3 ? '' : d + '.')), intervalMs);
    return () => clearInterval(id);
  }, [active, intervalMs]);
  return dots;
}

export function CourseGenerationActivity({
                                           active,
                                           startedAt,
                                           steps = DEFAULT_STEPS,
                                           maxVisible = 5,
                                           keepCompletedForSeconds = 18,
                                         }: {
  active: boolean;
  startedAt?: string;
  steps?: Step[];
  maxVisible?: number;
  keepCompletedForSeconds?: number;
}) {
  const baseStart = useMemo(
    () => (startedAt ? new Date(startedAt).getTime() : Date.now()),
    [startedAt]
  );

  const [elapsed, setElapsed] = useState(0);
  const dots = useAnimatedDots(active);

  useEffect(() => {
    if (!active) return;

    const tick = () => {
      const now = Date.now();
      setElapsed(Math.max(0, Math.floor((now - baseStart) / 1000)));
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [active, baseStart]);

  const currentStepIndex = useMemo(() => {
    let idx = 0;
    for (let i = 0; i < steps.length; i++) {
      if (elapsed >= steps[i].afterSeconds) idx = i;
    }
    return idx;
  }, [elapsed, steps]);

  const visibleSteps = useMemo(() => {
    const now = elapsed;

    // show current + recently completed (briefly), limited to maxVisible
    const recent = steps
      .map((s, i) => ({ ...s, index: i }))
      .filter(({ afterSeconds, index }) => {
        if (index === currentStepIndex) return true; // current step always shown
        if (index > currentStepIndex) return false; // never show future steps
        return now - afterSeconds <= keepCompletedForSeconds; // fade out older done steps
      });

    // ensure we always show a compact window even if keepCompletedForSeconds removes too much
    const fallback = steps
      .map((s, i) => ({ ...s, index: i }))
      .slice(Math.max(0, currentStepIndex - (maxVisible - 1)), currentStepIndex + 1);

    const merged = recent.length >= 2 ? recent : fallback;
    return merged.slice(-maxVisible);
  }, [elapsed, steps, currentStepIndex, maxVisible, keepCompletedForSeconds]);

  if (!active) return null;

  const current = steps[currentStepIndex];

  return (
    <Box>
      <Typography variant="subtitle2">Generating course</Typography>
      <Typography variant="caption" color="text.secondary">
        Working in the background — you can keep browsing.
      </Typography>

      <LinearProgress sx={{ mt: 1, mb: 1 }} />

      <Box sx={{ border: '1px solid #eee', borderRadius: 1, overflow: 'hidden' }}>
        <Box sx={{ px: 1.5, py: 1, bgcolor: 'rgba(0,0,0,0.02)' }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {current?.text}
            <Box component="span" sx={{ display: 'inline-block', minWidth: 16 }}>
              {dots}
            </Box>
          </Typography>
        </Box>

        {/* No scroll: the list is always short via maxVisible */}
        <List dense sx={{ m: 0 }}>
          {visibleSteps.map((s) => {
            const done = elapsed >= s.afterSeconds;
            const isCurrent = s.index === currentStepIndex;

            return (
              <ListItem
                key={`${s.afterSeconds}-${s.index}`}
                sx={{
                  opacity: done ? 0.95 : 0.55,
                  py: 0.5,
                }}
              >
                <ListItemText
                  primary={
                    <Typography variant="body2">
                      {done ? '✓ ' : isCurrent ? '→ ' : '• '}
                      {s.text}
                    </Typography>
                  }
                />
              </ListItem>
            );
          })}
        </List>
      </Box>
    </Box>
  );
}